// ─────────────────────────────────────────────────────────────
// UpstreamErrorBubble — friendly chat-stream error surface
//
// Renders the structured `UpstreamError` payload Sam emits on
// the SSE stream (Marie + Jamie) as a soft warning-tinted card
// inside the chat scroll. Reads clearly as "this is the upstream
// AI provider being unhappy" rather than "Maps Discover Studio
// crashed".
//
// Design intent (Greg, 2026-05-14):
//   • Naming both the model and the provider is mandatory —
//     so users understand that switching Opus 4.7 → Opus 4.6
//     won't help when Anthropic itself is throttling, but
//     switching to GPT-5 will.
//   • Soft warning tone, NOT alarming red — these are routine
//     transient failures, not catastrophes. Warning-tinted
//     surface + outlined icon, no exclamation marks in copy.
//   • Per-category copy hand-tuned for the failure mode.
//     `upstream_rate_limited` discourages a model swap on the
//     same family (same APIM quota) but suggests cross-provider.
//   • Sam's `error.message` is the safe English fallback for
//     any category we don't have richer copy for.
//
// IMPORTANT: this component is shared by Marie (`AiPanel.tsx`)
// and Jamie (`JamieChatView.tsx`). Any change here affects
// both — keep that intentional.
// ─────────────────────────────────────────────────────────────

import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import WifiOffOutlinedIcon from "@mui/icons-material/WifiOffOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import type { UpstreamError, UpstreamErrorCategory } from "../types";

interface UpstreamErrorBubbleProps {
  error: UpstreamError;
}

/** Per-category visuals + headline. The body copy is built dynamically
 *  so the model + provider names are always woven in. */
const CATEGORY_PRESETS: Record<
  UpstreamErrorCategory,
  { icon: SvgIconComponent; headline: (e: UpstreamError) => string }
> = {
  upstream_overloaded: {
    icon: CloudOffOutlinedIcon,
    headline: (e) => `${e.model_label} is overloaded right now.`,
  },
  upstream_rate_limited: {
    icon: HourglassEmptyOutlinedIcon,
    headline: (e) => `${e.model_label} hit a rate limit.`,
  },
  upstream_timeout: {
    icon: AccessTimeOutlinedIcon,
    headline: (e) => `${e.model_label} took too long to respond.`,
  },
  upstream_5xx: {
    icon: WarningAmberOutlinedIcon,
    headline: (e) => `${e.provider_label} returned an error.`,
  },
  upstream_network: {
    icon: WifiOffOutlinedIcon,
    headline: (e) => `Couldn't reach ${e.provider_label}.`,
  },
  upstream_truncated: {
    icon: CloudOffOutlinedIcon,
    headline: (e) => `${e.model_label}'s response was cut short.`,
  },
  upstream_unknown: {
    icon: ErrorOutlineOutlinedIcon,
    headline: (e) => `${e.model_label} hit an unexpected error.`,
  },
};

/** Build the explanatory body. Each branch names both the provider
 *  AND the model so the user knows where to switch from. */
function bodyCopy(error: UpstreamError): string {
  const { category, provider_label, model_label } = error;

  switch (category) {
    case "upstream_overloaded":
      return (
        `This is a ${provider_label} capacity issue, not a Maps Discover Studio bug. ` +
        `Wait a minute and try again, or switch to a model on a different provider in the picker above.`
      );
    case "upstream_rate_limited":
      return (
        `You've hit ${provider_label}'s per-minute rate limit. ` +
        `Wait about a minute and try again — switching to another ${provider_label} model usually won't help (same quota), ` +
        `but a model on a different provider will.`
      );
    case "upstream_timeout":
      return (
        `The request to ${provider_label} ran past its time budget. ` +
        `Try again, or simplify your prompt — long contexts and very large images make timeouts more likely.`
      );
    case "upstream_5xx":
      return (
        `${model_label} couldn't be reached due to a server-side error on ${provider_label}'s end. ` +
        `Try again, or switch to a different model.`
      );
    case "upstream_network":
      return (
        `The connection to ${provider_label} failed. ` +
        `Check your network and try again, or switch to a model on a different provider.`
      );
    case "upstream_truncated":
      return (
        `${provider_label} stopped sending mid-response — usually a transient capacity issue on their side, not a bug here. ` +
        `Try again, or switch to a different model.`
      );
    case "upstream_unknown":
      // Defer to Sam's safe English fallback — by definition we don't
      // have category-specific advice for the unknown bucket.
      return error.message;
  }
}

export function UpstreamErrorBubble({ error }: UpstreamErrorBubbleProps) {
  const preset = CATEGORY_PRESETS[error.category] ?? CATEGORY_PRESETS.upstream_unknown;
  const Icon = preset.icon;
  const headline = preset.headline(error);
  const body = bodyCopy(error);

  return (
    <Box
      role="alert"
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: "8px",
        // Soft warning surface — derived from the theme's `warning` role
        // via alpha() so the tint stays consistent with the rest of the
        // app's MD3 surface stack and follows light/dark mode automatically.
        // Avoids the alarming-red `error.main` tone because these failures
        // are routine and user-recoverable.
        backgroundColor: (theme) =>
          alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.08),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
        // Make sure long model/provider names wrap inside narrow
        // chat columns instead of bursting the bubble.
        minWidth: 0,
      }}
    >
      <Icon
        sx={{
          fontSize: 20,
          color: "warning.main",
          flexShrink: 0,
          mt: "1px",
        }}
        aria-hidden
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.4,
            wordBreak: "break-word",
          }}
        >
          {headline}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            color: "text.secondary",
            lineHeight: 1.5,
            fontSize: "0.8125rem",
            wordBreak: "break-word",
          }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}
